import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Package, Phone, MapPin, Home } from 'lucide-react';
import { trackPurchase } from '../utils/metaPixel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderSuccess = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      setOrder(response.data);
      // Meta Pixel — fire Purchase event once the order is confirmed
      trackPurchase(response.data);
    } catch (error) {
      console.error('Error fetching order:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-cyan-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-900 text-xl mb-4">Commande introuvable</p>
          <Link to="/" className="text-cyan-400 hover:text-cyan-300">
            Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-8">
      <div className="container mx-auto px-4 max-w-3xl">
        {/* Success Message */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce shadow-lg">
            <CheckCircle className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4" data-testid="order-success-title">
            Commande confirmée !
          </h1>
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-500 rounded-2xl p-6 max-w-xl mx-auto shadow-md">
            <p className="text-green-700 text-xl font-bold mb-3">
              ✓ Votre commande a été bien enregistrée
            </p>
            <p className="text-gray-900 text-lg font-semibold">
              📞 Vous allez être contacté par le magasin par téléphone
            </p>
          </div>
        </div>

        {/* Order Details */}
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Package className="w-5 h-5" />
            Détails de la commande
          </h2>
          
          <div className="space-y-3 mb-6">
            <div className="flex justify-between">
              <span className="text-gray-500">Numéro de commande</span>
              <span className="text-gray-900 font-mono" data-testid="order-id">{order.id.substring(0, 8).toUpperCase()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Date</span>
              <span className="text-gray-900">{new Date(order.created_at).toLocaleDateString('fr-FR')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Statut</span>
              <span className="text-yellow-500 font-semibold">En attente</span>
            </div>
          </div>

          {/* Items */}
          <div className="border-t border-gray-200 pt-4 mb-4">
            <h3 className="text-gray-900 font-semibold mb-3">Articles commandés</h3>
            <div className="space-y-2">
              {order.items.map((item) => (
                <div key={item.product_id} className="flex justify-between text-sm">
                  <span className="text-gray-500">
                    {item.product_name} x{item.quantity}
                  </span>
                  <span className="text-gray-900">
                    {(item.price * item.quantity).toLocaleString('fr-DZ')} DA
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4 flex justify-between text-gray-900 font-bold text-xl">
            <span>Total</span>
            <span data-testid="order-total">{order.total.toLocaleString('fr-DZ')} DA</span>
          </div>
        </div>

        {/* Customer Info */}
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Vos informations</h2>
          
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Phone className="w-5 h-5 text-cyan-400 mt-0.5" />
              <div>
                <div className="text-gray-500 text-sm">Téléphone</div>
                <div className="text-gray-900">{order.phone}</div>
              </div>
            </div>
            {order.delivery_method === 'delivery' ? (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-cyan-400 mt-0.5" />
                <div>
                  <div className="text-gray-500 text-sm">Adresse de livraison</div>
                  <div className="text-gray-900">{order.address}</div>
                  <div className="text-gray-900">{order.wilaya}</div>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <Home className="w-5 h-5 text-cyan-400 mt-0.5" />
                <div>
                  <div className="text-gray-500 text-sm">Retrait en magasin</div>
                  <div className="text-gray-900">N°13 Lotissement Mohamed Essadik Benyahia, Dar El Beïda</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-6 mb-6">
          <h3 className="text-blue-400 font-bold mb-3">Étapes suivantes</h3>
          <ol className="text-blue-300 text-sm space-y-2 list-decimal list-inside">
            <li>Notre équipe va vérifier votre commande</li>
            <li>Nous vous contacterons par téléphone pour confirmer</li>
            <li>{order.delivery_method === 'delivery' ? 'Livraison à votre adresse' : 'Préparez-vous pour le retrait en magasin'}</li>
            <li>Paiement à la réception</li>
          </ol>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Link
            to="/"
            className="flex-1 bg-cyan-500 text-white text-center font-semibold py-3 rounded-lg hover:bg-cyan-600 transition"
            data-testid="back-to-home"
          >
            Retour à l'accueil
          </Link>
          <Link
            to="/products"
            className="flex-1 bg-white text-gray-900 text-center font-semibold py-3 rounded-lg hover:bg-gray-200 transition"
          >
            Continuer mes achats
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;